import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http'; 
import { Globals } from '../objects/Globals';
import { Observable, Subject, forkJoin } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DetailProperty } from '../objects/DetailObject';
import { EnvService } from '../env.service';

@Injectable({
  providedIn: 'root'
})
export class DetailService {
  private loginUrl: string;
  private defaultId = '00000000-0000-0000-0000-000000000000';

  detail: Subject<boolean> = new Subject<boolean>();
  getDetailChanged$ = this.detail.asObservable();

  currentPropertyFocus: Subject<any> = new Subject<any>();
  currentPropertyFocusChanged$ = this.currentPropertyFocus.asObservable();
  
  currentLookup: Subject<any> = new Subject<any>();
  currentLookupChanged$ = this.currentLookup.asObservable();

  parentChild: Subject<any> = new Subject<any>();
  parentChildChanged$ = this.parentChild.asObservable();

  disableChild: Subject<any> = new Subject<any>();
  disableChild$ = this.disableChild.asObservable();

  treeData: Subject<any> = new Subject<any>();
  treeDataChanged = this.treeData.asObservable();

  loadMore: Subject<any> = new Subject<any>();
  loadMoreChanged = this.loadMore.asObservable();

  pendingUpdateProps: any = [];

  setLookup(name:string){
    this.currentLookup.next(name);
  }

  setPropertyFocus(name:string){
    this.currentPropertyFocus.next(name);
  }

  clearChild(prop:DetailProperty){
    this.parentChild.next(prop);
  }

  setDisableChild(prop){
    this.disableChild.next(prop);
  }

  setTreeData(data: any){
    this.treeData.next(data);
  }

  setLoadMore(data: any){
    this.loadMore.next(data);
  }

  isNewDetail(detail: boolean){
    this.detail.next(detail);
  }

  pushPendingUpdate(property: DetailProperty, value: any){
    if (property){
      var index = this.pendingUpdateProps.findIndex(x => x.id == property.PropertyId);
      if (index > -1) this.pendingUpdateProps.splice(index, 1);

      this.pendingUpdateProps.push({id: property.PropertyId, property: property, value: value});
    }
  }

  removePendingUpdate(propertyId: string){
    if (this.pendingUpdateProps.find(x => x.id == propertyId)) {
      this.pendingUpdateProps.splice(this.pendingUpdateProps.findIndex(x => x.id == propertyId));
    }
  }

  clearPendingUpdate(){
    this.pendingUpdateProps = [];
  }

  getPendingUpdate(){
    return this.pendingUpdateProps;
  }

  getDetail(id:string):Observable<string>{
    var path = this.loginUrl +"GetDetailCard/"+ id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  getAnonymousDetail(id: string):Observable<string>{
    var path = this.loginUrl +"GetDetailCard/"+ id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.defaultId, "Session": this.defaultId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  newDetail(type:string, id:string):Observable<string>{
    var path = this.loginUrl +"GetNewDetail/"+type+"/" + id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, '', httpOptions);
  }

  updateField(id:string, fieldName:string, fieldValue:string, selectionValue: string = ""):Observable<string>{
    var path = this.loginUrl +"DetailUpdatePropertyValue/"+id+"/" + fieldName + "?selectionValue=" + selectionValue;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, fieldValue, httpOptions);
  }

  updateMultiFields(id:string, fieldObj: any) {
    let getObs = [];
    for (let field of fieldObj) {
      var path = this.loginUrl +"DetailUpdatePropertyValue/"+id+"/" + field.name + "?selectionValue=" + field.selectionValue;
      var httpOptions = {
        headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
      };
      getObs.push(this.http.post<string>(`${path}`, field.value, httpOptions));
    }
    return forkJoin(getObs);
  }
  
  anonymousUpdateField(id:string, fieldName:string, fieldValue:string):Observable<string>{
    var path = this.loginUrl +"DetailUpdatePropertyValue/"+id+"/" + fieldName;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.defaultId, "Session": this.defaultId })
    };
    return this.http.post<string>(`${path}`, fieldValue, httpOptions);
  }

  detailAction(id:string, actionCommand:string):Observable<string>{
    var path = this.loginUrl +"DetailAction/"+id+"/" + actionCommand;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, '', httpOptions);
  }

  anonymousDetailAction(id:string, actionCommand: string){
    var path = this.loginUrl +"DetailAction/"+id+"/" + actionCommand;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.defaultId, "Session": this.defaultId })
    };
    return this.http.post<string>(`${path}`, '', httpOptions);
  }

  changeSection(id:string, sectionId:string):Observable<string>{
    var path = this.loginUrl +"ChangeSection/"+id+"/" + sectionId;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, '', httpOptions);
  }
  
  createInfoCard(id:string, name:string):Observable<string>{
    var path = this.loginUrl +"CreateInfoCard/"+id+"/" + name;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, '', httpOptions);
  }

  getInfoCard(id:string):Observable<string>{
    var path = this.loginUrl +"GetInfoCard/"+id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  getLookupList(id:string, propertyName:string, groupValue: string = ""){
    var path = this.loginUrl +"GetDropdownList/"+id+"/"+propertyName + "?groupValue=" + groupValue;

    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  searchDropdownList(id: string, propertyName:string, keyword:any){
    var path = this.loginUrl +"SearchDropdownList/" + id + "/" + propertyName + "?keyword=" + keyword;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text','Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  searchInDropdownList(id: string, propertyName:string, keyword:any){
    var path = this.loginUrl +"SearchInDropdownList/"+id+"/"+propertyName;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text','Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, keyword, httpOptions);
  }

  getHelp(id:string){
    var path = this.loginUrl +"GetHelp/"+id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  getFixedMessageText(id:string, propertyId:string){
    var path = this.loginUrl +"GetFixedPropertyValue/"+id+"/"+propertyId;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  getCurrentSection(id:string){
    var path = this.loginUrl +"GetCurrentSection/"+id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  requestSupplierRegistration(data){
    var path = this.loginUrl +"RequestSupplierRegistration";
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true' })
    };
    return this.http.post<string>(`${path}`,data, httpOptions);
  }

  getPreview(id:string, actionCommand:string){
    var path = this.loginUrl +"CreateDetailPreview/"+id+"/"+actionCommand;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, "", httpOptions);
  }

  getPreviewNextSection(id:string, sectionSize: string){
    var path = this.loginUrl +"CreatePreviewNextSection/"+id+"?sectionSize=" + sectionSize;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, "", httpOptions);
  }

  getSectionStatus(id:string, sectionId:string){
    var path = this.loginUrl +"GetSectionStatus/"+id+"/"+sectionId;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  getCurrentWorkspaceItems(id:string){
    var path = this.loginUrl +"GetCurrentWorkspaceItems/"+id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  getDateTimePropertyDateSegmentValue(id:string, propertyName:string){
    var path = this.loginUrl +"GetDateTimePropertyDateSegmentValue/"+id+"/"+propertyName;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  getDateTimePropertyTimeSegmentValue(id:string, propertyName:string){
    var path = this.loginUrl +"GetDateTimePropertyTimeSegmentValue/"+id+"/"+propertyName;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  getNewDetailFromPropertyGroup(detailCardId:string, propertyGroupId:string){
    var path = this.loginUrl +"GetNewDetailFromPropertyGroup/"+detailCardId+"/"+propertyGroupId;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, "", httpOptions);
  }

  checkActionPrompt(id:string, actionCommand:string){
    var path = this.loginUrl +"CheckActionPrompt/"+id+"/"+actionCommand;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, "", httpOptions);
  }

  newFormFromDetail(detailCardId:string, propertyGroupId:string){
    var path = this.loginUrl +"NewFormFromDetail/"+detailCardId+"/"+propertyGroupId;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, "", httpOptions);
  }

  getFormDetailCard(id:string):Observable<string>{
    var path = this.loginUrl +"GetFormDetailCard/"+id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  updateFormPropertyValue(id:string, propertyName:string, value: string){
    var path = this.loginUrl +"UpdateFormPropertyValue/"+id+"/"+propertyName;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, value, httpOptions);
  }

  updateFormPropertyRemarks(id:string, propertyName:string, value: string){
    var path = this.loginUrl +"UpdateFormPropertyRemarks/"+id+"/"+propertyName;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, value, httpOptions);
  }

  updateFormBaseRemarks(id:string, propertyName:string, value: string){
    var path = this.loginUrl +"UpdateFormPropertyBaseRemarks/"+id+"/"+propertyName;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, value, httpOptions);
  }

  actionFormDetailCard(id:string, actionCommand:string):Observable<string>{
    var path = this.loginUrl +"ActionFormDetailCard/"+id+"/" + actionCommand;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, '', httpOptions);
  }

  checkIfDetailHasUpdate(id: string):Observable<string>{
    var path = this.loginUrl +"CheckIfDetailHasUpdate/" + id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.get<string>(`${path}`, httpOptions);
  }

  clearSummarySelectedItem(id:string):Observable<string>{
    var path = this.loginUrl +"ClearSummarySelectedItem/" + id;
    var httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/text', 'Access-Control-Allow-Origin': 'true', "User": this.globals.Session.Session.UserId, "Session": this.globals.Session.Session.SessionId })
    };
    return this.http.post<string>(`${path}`, "", httpOptions);
  }

  constructor(private http:HttpClient, private globals:Globals, private env: EnvService) { this.loginUrl = this.env.servicePath }
}
